<template>
  <div class="order-manage">
    <PageHeader 
      title="퀵스타 간편 등록" 
      description="주문을 저장하고, 액셀로 일괄 다운로드합니다." 
      :margin-bottom="true"
    />

    <div class="content-container">
      <AppLoading v-if="loading" text="주문을 불러오는 중..." />

      <div class="list-section" v-else>
        <div class="action-bar">
          <div class="search-panel">
            <div class="search-panel__controls">
              <el-input
                v-model="settlementSearchKeyword"
                clearable
                placeholder="구매내역검색"
                class="search-panel__input"
                :disabled="settlementSearchLoading"
                @keyup.enter="handleSettlementSearch"
              />
              <el-button
                type="primary"
                :loading="settlementSearchLoading"
                :disabled="settlementSearchLoading"
                @click="handleSettlementSearch"
              >
                검색
              </el-button>
            </div>
            <div v-if="settlementSearchSearched" class="search-results">
              <div v-if="settlementSearchResults.length > 0" class="search-results__list">
                <a
                  v-for="(item, index) in settlementSearchResults"
                  :key="`${item.product_name}-${index}`"
                  class="search-result-card"
                  :href="getSettlementSearchHref(item)"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    v-if="item.image_url"
                    :src="item.image_url"
                    :alt="item.product_name"
                    class="search-result-card__image"
                  />
                  <div v-else class="search-result-card__image search-result-card__image--empty">
                    이미지 없음
                  </div>
                  <div class="search-result-card__content">
                    <div class="search-result-card__name">{{ item.product_name || '-' }}</div>
                    <div class="search-result-card__link">{{ getSettlementSearchHref(item) }}</div>
                  </div>
                </a>
              </div>
              <div v-else class="search-results__empty">검색 결과가 없습니다.</div>
            </div>
          </div>

          <div class="action-bar__buttons">
            <el-button
              plain
              :loading="excelUploadLoading"
              :disabled="excelUploadLoading || excelSubmitLoading || processLoading || exportLoading"
              @click="openExcelUpload"
            >
              액셀 업로드
            </el-button>
            <el-button
              type="primary"
              :loading="exportLoading"
              :disabled="exportLoading || processLoading || excelUploadLoading || excelSubmitLoading"
              @click="handleQuickstarExport"
            >
              액셀 다운로드 ( {{ applicationNumber }} )
            </el-button>
            <input
              ref="excelInputEl"
              type="file"
              accept=".xlsx,.xls"
              class="hidden-file-input"
              @change="handleExcelFileChange"
            />
          </div>
        </div>
        <div v-if="rows.length === 0" class="empty-state">
          <p class="empty-message">주문이 존재하지 않습니다</p>
        </div>
        <div v-else class="card-grid">
          <div
            v-for="row in rows"
            :key="getOrderCardKey(row)"
            class="order-card"
            :class="getOrderCardClass(row)"
            @click="openProcess(row)"
          >
            <div class="card-head">
              <div class="card-head__row">
                <el-tag :type="row.market === 'naver' ? 'success' : 'info'" effect="dark">
                  {{ marketLabel(row.market) }}
                </el-tag>
                <span
                  v-if="hasOrderMemo(row)"
                  class="order-memo-inline"
                  :title="String(row.memo || '')"
                >
                  {{ row.memo }}
                </span>
              </div>
            </div>
            <div class="card-body">
              <div class="meta">
                <div class="title">{{ row.productName }}</div>
                <div class="option-list">
                  <div
                    v-for="(line, index) in getVisibleOptionLines(row)"
                    :key="`${getOrderCardKey(row)}-option-${index}`"
                    class="opt"
                  >
                    {{ line }}
                  </div>
                  <div v-if="getHiddenOptionCount(row) > 0" class="opt option-more">
                    외 {{ getHiddenOptionCount(row) }}개 옵션
                  </div>
                </div>
                <div class="opt">유입경로: {{ row.inflowPath || '-' }}</div>
                <div class="sub">
                  <span :class="{ 'qty-highlight': getTotalQuantity(row) !== 1 }">총수량 {{ getTotalQuantity(row) }}</span>
                  · 매출 {{ formatSales(row.sales) }}
                </div>
              </div>
            </div>
            <div class="card-links">
              <div class="link-group">
                <el-link v-if="row.marketProductUrl" :href="row.marketProductUrl" target="_blank" type="primary" @click.stop>마켓</el-link>
                <el-link v-if="row.productUrl" :href="row.productUrl" target="_blank" @click.stop>원본</el-link>
              </div>
              <div class="card-actions">
                <el-button
                  plain
                  size="small"
                  :disabled="memoSaveLoading"
                  @click.stop="openMemoModal(row)"
                >
                  메모
                </el-button>
                <el-button
                  plain
                  size="small"
                  :disabled="cancelLoading"
                  @click.stop="openCancel(row)"
                >
                  주문취소
                </el-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <el-dialog
      v-model="memoModalVisible"
      title="상품 메모"
      width="480px"
      :close-on-click-modal="false"
      @close="onMemoModalClose"
    >
      <div class="memo-form">
        <div class="memo-form__field">
          <div class="memo-form__label">참고 URL</div>
          <el-input
            v-model="memoFormUrl"
            placeholder="https://..."
          />
        </div>
        <div class="memo-form__field">
          <div class="memo-form__label">특이사항 메모</div>
          <el-input
            v-model="memoFormMemo"
            type="textarea"
            :autosize="{ minRows: 3, maxRows: 8 }"
            placeholder="특이사항을 입력하세요"
          />
        </div>
      </div>
      <template #footer>
        <el-button :disabled="memoSaveLoading" @click="memoModalVisible = false">취소</el-button>
        <el-button type="primary" :loading="memoSaveLoading" @click="handleMemoSave">저장</el-button>
      </template>
    </el-dialog>

    <QuickstarsProcessModal
      :visible="processVisible"
      :order="activeOrder"
      :default-request-note="additionalMemo"
      :submitting="processLoading"
      @close="onProcessClose"
      @submit="handleProcessSubmit"
    />

    <OrderCancelModal
      :visible="cancelVisible"
      :order="cancelOrderRow"
      :submitting="cancelLoading"
      @close="onCancelClose"
      @submit="handleCancelSubmit"
    />

    <el-dialog
      v-model="excelModalVisible"
      title="액셀 업로드 미리보기"
      width="860px"
      :close-on-click-modal="false"
    >
      <div class="excel-summary">
        <span>파일: {{ excelUploadStats.fileName || '-' }}</span>
        <span>원본 행수: {{ excelUploadStats.rawRows }}</span>
        <span>유효 행수: {{ excelUploadStats.validRows }}</span>
        <span>업데이트 행 수: {{ excelUploadStats.uniqueRows }}</span>
      </div>
      <el-table :data="excelPreviewRows" stripe border height="360">
        <el-table-column type="index" label="#" width="56" />
        <el-table-column prop="deliveryFee" label="E(배송비)" min-width="140" />
        <el-table-column prop="groupNumber" label="G(그룹번호)" min-width="180" />
        <el-table-column prop="trackingNumber" label="J(운송장)" min-width="180" />
        <el-table-column prop="phoneNumber" label="P(전화번호)" min-width="180" />
      </el-table>
      <template #footer>
        <span class="dialog-footer">
          <el-button :disabled="excelSubmitLoading" @click="excelModalVisible = false">취소</el-button>
          <el-button
            type="primary"
            :loading="excelSubmitLoading"
            :disabled="excelPreviewRows.length === 0"
            @click="handleExcelRegister"
          >
            등록
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { ElMessage } from 'element-plus';
import * as XLSX from 'xlsx';
import AppLoading from '../../components/common/loading.vue';
import PageHeader from '@/components/common/header.vue';
import { fetchOrdersByUser, saveProductMeta, saveQuickstarExcelData, downloadQuickstarExcel, updateQuickstarTrackingByExcel, cancelOrder, searchSettlementProducts } from '@/services/order';
import QuickstarsProcessModal from '../../components/modal/quickstars.vue';
import OrderCancelModal from '../../components/modal/orderCancelModal.vue';

export default {
  name: 'QuickstarsOrderManage',
  components: { AppLoading, PageHeader, QuickstarsProcessModal, OrderCancelModal },
  setup() {
    const MAX_VISIBLE_OPTION_LINES = 2;
    const loading = ref(false);
    const rows = ref([]);
    const processVisible = ref(false);
    const activeOrder = ref(null);
    const processLoading = ref(false);
    const exportLoading = ref(false);
    const applicationNumber = ref(0);
    const additionalMemo = ref('');
    const cancelVisible = ref(false);
    const cancelOrderRow = ref(null);
    const cancelLoading = ref(false);
    const excelInputEl = ref(null);
    const excelUploadLoading = ref(false);
    const excelSubmitLoading = ref(false);
    const excelModalVisible = ref(false);
    const excelPreviewRows = ref([]);
    const excelUploadStats = ref({
      fileName: '',
      rawRows: 0,
      validRows: 0,
      uniqueRows: 0,
      duplicateCount: 0
    });
    const settlementSearchKeyword = ref('');
    const settlementSearchLoading = ref(false);
    const settlementSearchResults = ref([]);
    const settlementSearchSearched = ref(false);
    const memoModalVisible = ref(false);
    const memoTargetRow = ref(null);
    const memoFormUrl = ref('');
    const memoFormMemo = ref('');
    const memoSaveLoading = ref(false);

    const formatSales = (sales) => {
      const original = Number(sales);
      if (Number.isNaN(original) || original === null || original === undefined) return '-';
      return original.toLocaleString();
    };

    const marketLabel = (market) => {
      if (market === 'naver') return '네이버';
      return '기타';
    };

    const getOrderItems = (row) => {
      return Array.isArray(row?.items) ? row.items : [];
    };

    const getTotalQuantity = (row) => {
      const total = getOrderItems(row).reduce((sum, item) => {
        const qty = Number(item?.quantity);
        return sum + (Number.isNaN(qty) ? 0 : qty);
      }, 0);

      return total > 0 ? total : 0;
    };

    const formatOptionLine = (item) => {
      const optionText = item?.productOption || '옵션없음';
      const qty = Number(item?.quantity);
      const qtyText = Number.isNaN(qty) || qty <= 0 ? '-' : qty;
      return `${optionText} x ${qtyText}`;
    };

    const getVisibleOptionLines = (row) => {
      return getOrderItems(row)
        .slice(0, MAX_VISIBLE_OPTION_LINES)
        .map(formatOptionLine);
    };

    const getHiddenOptionCount = (row) => {
      const items = getOrderItems(row);
      return items.length > MAX_VISIBLE_OPTION_LINES ? items.length - MAX_VISIBLE_OPTION_LINES : 0;
    };

    const getOrderCardKey = (row) => {
      const ids = Array.isArray(row?.productOrderIds) ? row.productOrderIds : [];
      return ids.join('-');
    };

    const hasOrderMemo = (row) => {
      return String(row?.memo ?? '').trim() !== '';
    };

    const getOrderCardClass = (row) => ({
      alert: getTotalQuantity(row) !== 1 && !hasOrderMemo(row),
      'order-card--memo': hasOrderMemo(row)
    });

    const parseProductIdFromSellerCode = (sellerProductCode) => {
      if (sellerProductCode == null || sellerProductCode === '') return null;
      const s = String(sellerProductCode).trim();
      const matched = s.match(/^n2n(.+)$/i);
      return matched ? matched[1].trim() : s;
    };

    const toProductMetaId = (raw) => {
      if (raw == null || raw === '') return null;
      if (/^\d+$/.test(String(raw))) return Number(raw);
      return String(raw);
    };

    const getSettlementSearchHref = (item) => {
      return item?.url || item?.image_url || '';
    };

    const resetSettlementSearchResults = () => {
      settlementSearchResults.value = [];
      settlementSearchSearched.value = false;
    };

    const loadOrders = async () => {
      resetSettlementSearchResults();
      loading.value = true;
      try {
        const res = await fetchOrdersByUser();
        if (res?.success) {
          const raw = Array.isArray(res.orders) ? res.orders : [];
          rows.value = raw;
          applicationNumber.value = Number(res?.applicationNumber) || 0;
          additionalMemo.value = res?.additional_memo ? String(res.additional_memo) : '';
        } else {
          rows.value = [];
          applicationNumber.value = 0;
          additionalMemo.value = '';
        }
      } catch (e) {
        rows.value = [];
        applicationNumber.value = 0;
        additionalMemo.value = '';
        ElMessage.error(e?.response?.data?.message || '주문 조회 중 오류가 발생했습니다.');
      } finally {
        loading.value = false;
      }
    };

    const openProcess = (row) => {
      activeOrder.value = row;
      processVisible.value = true;
    };

    const onProcessClose = () => {
      processVisible.value = false;
      processLoading.value = false;
      activeOrder.value = null;
    };

    const openCancel = (row) => {
      cancelOrderRow.value = row;
      cancelVisible.value = true;
    };

    const openMemoModal = (row) => {
      memoTargetRow.value = row;
      memoFormUrl.value = row?.productUrl != null && row.productUrl !== '' ? String(row.productUrl) : '';
      memoFormMemo.value = row?.memo != null && row.memo !== '' ? String(row.memo) : '';
      memoModalVisible.value = true;
    };

    const onMemoModalClose = () => {
      memoTargetRow.value = null;
      memoFormUrl.value = '';
      memoFormMemo.value = '';
    };

    const handleMemoSave = async () => {
      if (memoSaveLoading.value) return;
      const target = memoTargetRow.value;
      const rawId = parseProductIdFromSellerCode(target?.sellerProductCode);
      const productid = toProductMetaId(rawId);
      if (productid == null || productid === '') {
        ElMessage.warning('상품코드(sellerProductCode)가 없어 저장할 수 없습니다.');
        return;
      }

      memoSaveLoading.value = true;
      try {
        const res = await saveProductMeta({
          productid,
          url: memoFormUrl.value,
          memo: memoFormMemo.value
        });
        if (res?.success) {
          ElMessage.success(res?.message || '메모가 저장되었습니다.');
          memoModalVisible.value = false;
          await loadOrders();
        } else {
          ElMessage.error(res?.message || '메모 저장에 실패했습니다.');
        }
      } catch (e) {
        ElMessage.error(e?.response?.data?.message || e?.message || '메모 저장 중 오류가 발생했습니다.');
      } finally {
        memoSaveLoading.value = false;
      }
    };

    const onCancelClose = () => {
      cancelVisible.value = false;
      cancelLoading.value = false;
      cancelOrderRow.value = null;
    };

    const handleProcessSubmit = async (payload) => {
      if (processLoading.value) return;
      processLoading.value = true;
      try {
        const res = await saveQuickstarExcelData(payload);
        if (res?.success) {
          const appNo = res?.applicationNumber;
          const insertedRows = res?.insertedRows;
          const suffix = (appNo || insertedRows)
            ? ` (신청번호: ${appNo ?? '-'}, 저장건수: ${insertedRows ?? '-'})`
            : '';
          ElMessage.success((res?.message || '퀵스타 주문이 저장되었습니다.') + suffix);
          processVisible.value = false;
          activeOrder.value = null;
          await loadOrders();
        } else {
          const apiMsg = res?.data?.message || res?.message;
          ElMessage.error(apiMsg || '퀵스타 저장 요청이 거부되었습니다. 입력값을 확인하세요.');
        }
      } catch (e) {
        ElMessage.error(e?.response?.data?.message || e?.message || '퀵스타 저장 중 오류가 발생했습니다.');
      } finally {
        processLoading.value = false;
      }
    };

    const handleCancelSubmit = async (payload) => {
      if (cancelLoading.value) return;
      const target = cancelOrderRow.value;
      const markerNumber = Number(target?.marketNumber);
      const productOrderIds = Array.isArray(target?.productOrderIds) ? target.productOrderIds : [];

      if (!target) {
        ElMessage.warning('취소할 주문이 없습니다.');
        return;
      }
      if (!Number.isFinite(markerNumber) || markerNumber <= 0) {
        ElMessage.warning('마켓 번호가 없어 주문취소를 진행할 수 없습니다.');
        return;
      }
      if (productOrderIds.length === 0) {
        ElMessage.warning('취소할 주문번호가 없습니다.');
        return;
      }

      cancelLoading.value = true;
      try {
        const res = await cancelOrder({
          markerNumber,
          productOrderIds,
          code: payload.code,
          cancelDetailedReason: payload.cancelDetailedReason,
        });

        const results = Array.isArray(res?.results) ? res.results : [];
        const successCount = results.filter((item) => item?.success).length;
        const failCount = results.length - successCount;

        if (res?.success || successCount > 0) {
          const suffix = results.length
            ? ` (성공 ${successCount}건${failCount > 0 ? `, 실패 ${failCount}건` : ''})`
            : '';
          ElMessage.success((res?.message || '주문취소가 접수되었습니다.') + suffix);
          cancelVisible.value = false;
          cancelOrderRow.value = null;
          await loadOrders();
          return;
        }

        ElMessage.error(res?.message || '주문취소 요청이 거부되었습니다.');
      } catch (e) {
        ElMessage.error(e?.response?.data?.message || e?.message || '주문취소 중 오류가 발생했습니다.');
      } finally {
        cancelLoading.value = false;
      }
    };

    const handleQuickstarExport = async () => {
      if (exportLoading.value) return;
      exportLoading.value = true;
      try {
        const { blob, filename, rowCount, deletedCount } = await downloadQuickstarExcel();
        const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/vnd.ms-excel' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename || `quickstar_${Date.now()}.xls`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        const msg = `액셀 다운로드 완료 (row: ${rowCount || 0}, 삭제: ${deletedCount || 0})`;
        ElMessage.success(msg);
      } catch (e) {
        if (e?.response?.status === 404) {
          ElMessage.warning('저장된 액셀이 없습니다.');
        } else {
          ElMessage.error(e?.response?.data?.message || e?.message || '퀵스타 액셀 다운로드 중 오류가 발생했습니다.');
        }
      } finally {
        exportLoading.value = false;
      }
    };

    const handleSettlementSearch = async () => {
      if (settlementSearchLoading.value) return;

      const productName = settlementSearchKeyword.value.trim();
      if (!productName) {
        ElMessage.warning('상품명을 입력하세요.');
        return;
      }

      settlementSearchLoading.value = true;
      try {
        const res = await searchSettlementProducts({
          productName,
          limit: 20
        });
        settlementSearchResults.value = Array.isArray(res?.data) ? res.data : [];
        settlementSearchSearched.value = true;
      } catch (e) {
        settlementSearchResults.value = [];
        settlementSearchSearched.value = true;
        ElMessage.error(e?.response?.data?.message || e?.message || '정산 상품 검색 실패');
      } finally {
        settlementSearchLoading.value = false;
      }
    };

    const E_COL_IDX = 4;
    const G_COL_IDX = 6;
    const J_COL_IDX = 9;
    const P_COL_IDX = 15;
    const TRACKING_PRIMARY_LENGTH = 12;

    const normalizeCell = (value) => {
      if (value === null || value === undefined) return '';
      return String(value).trim();
    };

    const getWorksheetCellValue = (worksheet, rowIndex, colIndex, options = {}) => {
      const address = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex });
      const cell = worksheet[address];
      if (!cell) return '';
      const preferRawString = Boolean(options.preferRawString);
      const rawValue = cell.v;
      const formattedValue = cell.w;

      if (preferRawString && typeof rawValue === 'string') {
        return normalizeCell(rawValue);
      }
      if (formattedValue !== null && formattedValue !== undefined) {
        return normalizeCell(formattedValue);
      }
      return normalizeCell(rawValue);
    };

    const normalizeTrackingCell = (value) => {
      const raw = normalizeCell(value);
      if (!raw) return '';
      const normalizedRaw = raw.replace(/\u00A0/g, ' ').replace(/\t/g, ' ');
      const segments = normalizedRaw
        .split(/[,，\n\r]/)
        .map((segment) => segment.trim())
        .filter(Boolean);
      const tokens = segments.flatMap((segment) => segment.split(/\s+/).map((token) => token.trim()).filter(Boolean));
      if (tokens.length === 0) return '';
      const isScientific = (token) => /^[+-]?\d+(?:\.\d+)?[eE][+-]?\d+$/.test(token);
      const firstNormalToken = tokens.find((token) => !isScientific(token));
      if (firstNormalToken) {
        const compact = firstNormalToken.replace(/\D/g, '');
        if (compact.length >= TRACKING_PRIMARY_LENGTH * 2) {
          return compact.slice(0, TRACKING_PRIMARY_LENGTH);
        }
        return firstNormalToken;
      }

      const scientificToPlainDigits = (token) => {
        const match = token.match(/^([+-]?\d+(?:\.\d+)?)[eE]([+-]?\d+)$/);
        if (!match) return '';
        const mantissaRaw = match[1];
        const exponent = Number(match[2]);
        if (Number.isNaN(exponent)) return '';
        const mantissa = mantissaRaw.replace(/^[+-]/, '');
        const parts = mantissa.split('.');
        const intPart = parts[0] || '';
        const fracPart = parts[1] || '';
        const digits = `${intPart}${fracPart}`.replace(/^0+/, '') || '0';
        const decimalPos = intPart.length;
        const point = decimalPos + exponent;
        if (point <= 0) return '0';
        if (point >= digits.length) return `${digits}${'0'.repeat(point - digits.length)}`;
        return digits.slice(0, point);
      };

      const expandedDigits = scientificToPlainDigits(tokens[0]);
      if (!expandedDigits) return tokens[0];
      if (expandedDigits.length >= TRACKING_PRIMARY_LENGTH) {
        return expandedDigits.slice(0, TRACKING_PRIMARY_LENGTH);
      }
      return expandedDigits;
    };

    const normalizePhoneCell = (value) => {
      const raw = normalizeCell(value);
      if (!raw) return '';
      const normalized = raw.replace(/\u00A0/g, ' ').replace(/[-\s]/g, '');
      if (/^10\d*$/.test(normalized)) return `0${normalized}`;
      return normalized;
    };

    const normalizeDeliveryFeeCell = (value) => {
      return normalizeCell(value);
    };

    const openExcelUpload = () => {
      if (excelUploadLoading.value || excelSubmitLoading.value) return;
      if (excelInputEl.value) {
        excelInputEl.value.value = '';
        excelInputEl.value.click();
      }
    };

    const handleExcelFileChange = async (event) => {
      const file = event?.target?.files?.[0];
      if (!file) return;
      excelUploadLoading.value = true;
      try {
        const fileName = (file?.name || '').toLowerCase();
        if (!fileName.endsWith('.xlsx') && !fileName.endsWith('.xls')) {
          ElMessage.warning('엑셀 파일(.xlsx, .xls)만 업로드 가능합니다.');
          return;
        }

        const arrayBuffer = await file.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const firstSheetName = workbook.SheetNames?.[0];
        if (!firstSheetName) {
          ElMessage.warning('시트를 찾을 수 없습니다.');
          return;
        }

        const worksheet = workbook.Sheets[firstSheetName];
        const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1:A1');
        const startRowIndex = Math.max(range.s.r + 1, 1);
        const endRowIndex = range.e.r;
        const rawRows = [];
        for (let rowIndex = startRowIndex; rowIndex <= endRowIndex; rowIndex += 1) {
          rawRows.push({
            deliveryFeeRaw: getWorksheetCellValue(worksheet, rowIndex, E_COL_IDX),
            groupRaw: getWorksheetCellValue(worksheet, rowIndex, G_COL_IDX),
            trackingRaw: getWorksheetCellValue(worksheet, rowIndex, J_COL_IDX, { preferRawString: true }),
            phoneRaw: getWorksheetCellValue(worksheet, rowIndex, P_COL_IDX)
          });
        }

        const uniqueByGroup = new Map();
        let validRows = 0;
        let duplicateCount = 0;

        rawRows.forEach((row) => {
          const deliveryFee = normalizeDeliveryFeeCell(row.deliveryFeeRaw); // E
          const groupNumber = normalizeCell(row.groupRaw); // G
          const trackingNumber = normalizeTrackingCell(row.trackingRaw); // J
          const phoneNumber = normalizePhoneCell(row.phoneRaw); // P

          if (!groupNumber) return;
          validRows += 1;
          if (uniqueByGroup.has(groupNumber)) {
            duplicateCount += 1;
            return;
          }

          uniqueByGroup.set(groupNumber, {
            deliveryFee,
            groupNumber,
            trackingNumber,
            phoneNumber
          });
        });

        excelPreviewRows.value = Array.from(uniqueByGroup.values());
        excelUploadStats.value = {
          fileName: file.name || '',
          rawRows: rawRows.length,
          validRows,
          uniqueRows: excelPreviewRows.value.length,
          duplicateCount
        };

        if (excelPreviewRows.value.length === 0) {
          ElMessage.warning('등록 가능한 데이터가 없습니다. (G열 기준)');
          return;
        }
        excelModalVisible.value = true;
      } catch (e) {
        ElMessage.error(e?.message || '엑셀 파싱 중 오류가 발생했습니다.');
      } finally {
        excelUploadLoading.value = false;
        if (excelInputEl.value) excelInputEl.value.value = '';
      }
    };

    const handleExcelRegister = async () => {
      if (excelSubmitLoading.value) return;
      if (excelPreviewRows.value.length === 0) {
        ElMessage.warning('등록할 데이터가 없습니다.');
        return;
      }
      excelSubmitLoading.value = true;
      try {
        const payload = {
          rows: excelPreviewRows.value.map((row) => ({
            deliveryFee: row.deliveryFee,
            groupNumber: row.groupNumber,
            trackingNumber: row.trackingNumber,
            phoneNumber: row.phoneNumber
          }))
        };
        const res = await updateQuickstarTrackingByExcel(payload);
        if (res?.success) {
          const updatedRows = Number(res?.updatedRows || 0) || excelPreviewRows.value.length;
          ElMessage.success(`${updatedRows}건 업데이트되었습니다.`);
          excelModalVisible.value = false;
          excelPreviewRows.value = [];
        } else {
          ElMessage.error(res?.message || '퀵스타 운송장 업데이트가 실패했습니다.');
        }
      } catch (e) {
        ElMessage.error(e?.response?.data?.message || e?.message || '퀵스타 운송장 업데이트 중 오류가 발생했습니다.');
      } finally {
        excelSubmitLoading.value = false;
      }
    };

    onMounted(async () => {
      await loadOrders();
    });

    return {
      loading,
      rows,
      activeOrder,
      marketLabel,
      formatSales,
      getVisibleOptionLines,
      getHiddenOptionCount,
      getTotalQuantity,
      getOrderCardKey,
      getSettlementSearchHref,
      resetSettlementSearchResults,
      loadOrders,
      openProcess,
      onProcessClose,
      processVisible,
      handleProcessSubmit,
      processLoading,
      additionalMemo,
      applicationNumber,
      cancelVisible,
      cancelOrderRow,
      cancelLoading,
      openCancel,
      onCancelClose,
      handleCancelSubmit,
      exportLoading,
      handleQuickstarExport,
      excelInputEl,
      excelUploadLoading,
      excelSubmitLoading,
      excelModalVisible,
      excelPreviewRows,
      excelUploadStats,
      openExcelUpload,
      handleExcelFileChange,
      handleExcelRegister,
      settlementSearchKeyword,
      settlementSearchLoading,
      settlementSearchResults,
      settlementSearchSearched,
      handleSettlementSearch,
      hasOrderMemo,
      getOrderCardClass,
      memoModalVisible,
      memoFormUrl,
      memoFormMemo,
      memoSaveLoading,
      openMemoModal,
      onMemoModalClose,
      handleMemoSave,
    };
  }
}
</script>

<style scoped>
.order-manage {
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: var(--el-bg-color-page);
}

.content-container {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.list-section {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

.action-bar {
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin: 4px 4px 12px;
  overflow: visible;
}

.action-bar__buttons {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-shrink: 0;
}

.search-panel {
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
  max-width: 520px;
  z-index: 20;
}

.search-panel__controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.search-panel__input {
  max-width: 420px;
}

.search-results {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  width: min(720px, calc(100vw - 48px));
  max-width: 100%;
  max-height: 420px;
  overflow-y: auto;
  padding: 12px;
  border: 1px solid var(--el-border-color-light);
  border-radius: 12px;
  background: var(--el-bg-color);
  box-shadow: 0 12px 28px rgba(0, 0, 0, .14);
}

.search-results__list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.search-results__empty {
  padding: 16px 12px;
  border: 1px dashed var(--el-border-color);
  border-radius: 10px;
  color: var(--el-text-color-secondary);
  font-size: 13px;
}

.search-result-card {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  padding: 10px;
  border: 1px solid var(--el-border-color-lighter);
  border-radius: 10px;
  text-decoration: none;
  color: inherit;
  transition: border-color .2s ease, transform .2s ease, box-shadow .2s ease;
}

.search-result-card:hover {
  transform: translateY(-1px);
  border-color: var(--el-color-primary-light-5);
  box-shadow: 0 6px 18px rgba(0, 0, 0, .08);
}

.search-result-card__image {
  width: 56px;
  height: 56px;
  border-radius: 8px;
  object-fit: cover;
  flex-shrink: 0;
  border: 1px solid var(--el-border-color-lighter);
  background: var(--el-fill-color-light);
}

.search-result-card__image--empty {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--el-text-color-secondary);
  font-size: 11px;
}

.search-result-card__content {
  min-width: 0;
}

.search-result-card__name {
  font-size: 13px;
  font-weight: 600;
  color: var(--el-text-color-primary);
  line-height: 1.4;
  word-break: break-all;
}

.search-result-card__link {
  margin-top: 4px;
  font-size: 12px;
  color: var(--el-color-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.hidden-file-input {
  display: none;
}

.excel-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  margin-bottom: 12px;
  color: var(--el-text-color-regular);
  font-size: 13px;
}

.data-table {
  width: 100%;
}

.card-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  padding: 4px;
}

.order-card {
  background: var(--el-bg-color);
  border: 1px solid var(--el-border-color-light);
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  height: 100%;
  cursor: pointer;
  transition: all .25s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 3px rgba(0,0,0,.04);
}

.order-card.alert { 
  background: linear-gradient(135deg, #e8f4f8 0%, #f0f9fb 100%);
  border-color: var(--el-color-info-light-5);
}

.order-card--memo {
  background: linear-gradient(135deg, #fff5f5 0%, #ffecec 100%);
  border-color: #f0a0a0;
  box-shadow: 0 1px 3px rgba(200, 40, 40, 0.12);
}

.order-card--memo:hover {
  border-color: #e85d5d;
  box-shadow: 0 8px 24px rgba(200, 40, 40, 0.18);
}

.card-head__row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex-wrap: wrap;
}

.order-memo-inline {
  flex: 1;
  min-width: 0;
  font-size: 12px;
  line-height: 1.4;
  color: var(--el-color-danger);
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card-actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.memo-form__field {
  margin-bottom: 16px;
}
.memo-form__field:last-child {
  margin-bottom: 0;
}
.memo-form__label {
  font-size: 13px;
  color: var(--el-text-color-regular);
  margin-bottom: 6px;
}

.order-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0,0,0,.12);
  border-color: var(--el-color-primary-light-5);
}

.card-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.card-body { 
  display: flex; 
  flex: 1 1 auto;
  gap: 12px;
  margin-bottom: 12px;
}

.thumb { 
  width: 56px; 
  height: 56px; 
  border-radius: 8px; 
  overflow: hidden; 
  border: 1px solid var(--el-border-color-lighter); 
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0,0,0,.06);
}
.thumb img { 
  width: 100%; 
  height: 100%; 
  object-fit: cover; 
}

.meta { 
  flex: 1; 
  min-width: 0; 
}
.meta .title { 
  font-weight: 600;
  white-space: nowrap; 
  overflow: hidden; 
  text-overflow: ellipsis; 
  font-size: 15px;
  color: var(--el-text-color-primary);
  margin-bottom: 4px;
  line-height: 1.4;
}
.meta .opt { 
  color: var(--el-text-color-regular); 
  font-size: 13px; 
  white-space: nowrap; 
  overflow: hidden; 
  text-overflow: ellipsis;
  margin-bottom: 6px;
}
.option-list {
  margin-bottom: 2px;
}
.option-more {
  color: var(--el-text-color-secondary);
}
.meta .sub { 
  color: var(--el-text-color-secondary); 
  font-size: 12px; 
  line-height: 1.6;
}
.qty-highlight { 
  color: var(--el-color-primary); 
  font-weight: 600;
  background: var(--el-color-primary-light-9);
  padding: 2px 6px;
  border-radius: 4px;
}

.card-links { 
  display: flex; 
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: auto;
  padding-top: 12px;
  border-top: 1px solid var(--el-border-color-lighter);
}

.link-group {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 300px;
}

.empty-message {
  font-size: var(--el-font-size-large);
  color: var(--el-text-color-secondary);
  margin: 0;
}

@media (max-width: 768px) {
  .content-container { padding: var(--spacing-sm); }
  .action-bar {
    flex-direction: column;
  }
  .action-bar__buttons {
    width: 100%;
    justify-content: flex-start;
    flex-wrap: wrap;
  }
  .search-panel {
    width: 100%;
    max-width: none;
  }
  .search-panel__controls {
    flex-direction: column;
    align-items: stretch;
  }
  .search-panel__input {
    max-width: none;
  }
  .search-results__list {
    grid-template-columns: 1fr;
  }
  .card-grid { grid-template-columns: 1fr; }
  .card-links {
    flex-wrap: wrap;
    align-items: flex-start;
  }
}
</style>

